import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const SiteContentContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DEFAULT_CONTENT = {
  hero: {
    deck:
      "RTAMA LTD is an independent management studio based in Cyprus — building creator careers across every platform, every timezone, every algorithm.",
    stat_1_value: "120+",
    stat_1_label: "Creators",
    stat_2_value: "800M",
    stat_2_label: "Monthly Views",
    stat_3_value: "€42M",
    stat_3_label: "Brand deals",
    polaroid_1_caption: "@nova.rae 📍 LA",
    polaroid_2_caption: "live · 12.3k watching",
    starburst_year: "2022",
    handwritten_note: "no cap, just contracts ↘",
  },
  services: {
    intro:
      "A full-stack studio for creators who refuse to disappear when the algorithm shifts. No upsell. No fluff. Just the work.",
    items: [
      { title: "Talent Management", body: "End-to-end career architecture. Contracts, scheduling, brand fit, financial planning, and all the unglamorous admin we handle so you don't.", note: "we sit in your corner ✶" },
      { title: "Brand Partnerships", body: "We negotiate paid partnerships with the brands that match your values — and your numbers. Single-post to seven-figure annual deals.", note: "the receipts ➜" },
      { title: "Content Strategy", body: "Editorial calendars, format R&D, and intellectual property that compounds. Stop chasing the algorithm — start owning a category.", note: "own a category!" },
      { title: "Press & PR", body: "From local features to global covers. Strategic press placement and crisis comms — keeping your name in the right rooms.", note: "the right rooms 🗞" },
      { title: "Tour, Live & IRL", body: "Meet-and-greets, brand activations, live tours, conventions. We turn online attention into real-world events with real margins.", note: "online → IRL" },
      { title: "Legal & Operations", body: "Royalties, copyright, trademark, international tax. We sit with your lawyers and accountants so the back office is bulletproof.", note: "bulletproof" },
    ],
  },
  worldwide: {
    body:
      "RTAMA LTD is registered and operated from Limassol, Cyprus — a permanent home in the Mediterranean for a business that travels constantly. Our roster, our brand partners, and our affiliates span six continents. No matter the platform, no matter the timezone — we're already on the call.",
    stamp_year: "since 2022",
    spec_office: "Cyprus, EU",
    spec_markets: "32 countries",
    spec_coverage: "24 / 7 / 365",
    spec_travel: "Anywhere",
    cities: [
      { city: "Limassol", country: "Cyprus", tag: "HQ", hot: true },
      { city: "London", country: "UK", tag: "Affiliate", hot: false },
      { city: "Madrid", country: "Spain", tag: "Affiliate", hot: false },
      { city: "Los Angeles", country: "USA", tag: "Affiliate", hot: false },
      { city: "New York", country: "USA", tag: "Affiliate", hot: false },
      { city: "Mexico City", country: "Mexico", tag: "Affiliate", hot: false },
      { city: "São Paulo", country: "Brazil", tag: "Affiliate", hot: false },
      { city: "Berlin", country: "Germany", tag: "Affiliate", hot: false },
      { city: "Dubai", country: "UAE", tag: "Affiliate", hot: false },
      { city: "Singapore", country: "SG", tag: "Affiliate", hot: false },
    ],
  },
  contact: {
    intro:
      "Creator looking for representation? Brand ready to launch? Reply within 24h, every platform you produce for.",
    email: "rtamaltd@gmail.com",
    address_line_1: "RTAMA LTD",
    address_line_2: "Limassol, Cyprus · European Union",
  },
};

export function SiteContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/site-content`);
      setContent({
        hero: { ...DEFAULT_CONTENT.hero, ...(data?.hero || {}) },
        services: { ...DEFAULT_CONTENT.services, ...(data?.services || {}) },
        worldwide: { ...DEFAULT_CONTENT.worldwide, ...(data?.worldwide || {}) },
        contact: { ...DEFAULT_CONTENT.contact, ...(data?.contact || {}) },
      });
    } catch {
      // keep defaults
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <SiteContentContext.Provider value={{ content, loaded, refresh }}>
      {children}
    </SiteContentContext.Provider>
  );
}

export const useSiteContent = () => {
  const ctx = useContext(SiteContentContext);
  if (!ctx) return { content: DEFAULT_CONTENT, loaded: false, refresh: async () => {} };
  return ctx;
};
